import React, { useState, useEffect, useContext, useCallback } from 'react';
import { firestore } from '../Firebase';
import { serverTimestamp } from 'firebase/firestore';
import { AuthContextPublic } from '../context/AuthContextPublic';
import { useParams } from 'react-router-dom';
import './EmojiReactions.css'; // We'll create this CSS file next

const EmojiReactions = ({ features }) => {
  const [showEmojis, setShowEmojis] = useState(false);
  const [showFlyingEmojis, setShowFlyingEmojis] = useState(true); // Control whether flying emojis are displayed
  const [flyingEmojis, setFlyingEmojis] = useState([]);
  const { videoID } = useParams();
  const { userID, userName: contextUserName } =
    useContext(AuthContextPublic) || {};
  const [userName, setUserName] = useState('Anonymous');

  // Get and store user name
  useEffect(() => {
    if (contextUserName) {
      console.log('Using userName from context:', contextUserName);
      setUserName(contextUserName);
    } else if (userID) {
      // Try to fetch user's display name from Firebase if not in context
      console.log('No userName in context, trying to fetch from Firebase');
      firestore
        .collection('viewers')
        .doc(userID)
        .get()
        .then((doc) => {
          if (doc.exists && doc.data().userName) {
            const fetchedName = doc.data().userName;
            console.log('Fetched userName from Firebase:', fetchedName);
            setUserName(fetchedName);
          }
        })
        .catch((error) => {
          console.error('Error fetching user name:', error);
        });
    }
  }, [userID, contextUserName]);

  // Debug log to verify userID
  useEffect(() => {
    console.log(
      'EmojiReactions component mounted. UserID:',
      userID,
      'Name:',
      userName,
      'Context userName:',
      contextUserName
    );
  }, [userID, userName, contextUserName]);

  const emojis = [
    { id: 'heart', emoji: '❤️', label: 'Heart' },
    { id: 'thumbs-up', emoji: '👍', label: 'Thumbs Up' },
    { id: 'smile', emoji: '😀', label: 'Smile' },
    { id: 'applause', emoji: '👏', label: 'Applause' },
    { id: 'openmouth', emoji: '😮', label: 'Surprised' },
  ];

  // Generate random trajectory values
  const getTrajectory = useCallback(() => {
    // Random horizontal destination (relative to start)
    // Much wider spread for "45 degree" shooting angle
    // -500px (left) to +200px (right) - biased left since we are on the right side
    const targetX = Math.floor(Math.random() * 700) - 500; 
    
    // Random rotation (spin)
    // -45deg to +45deg
    const rotation = Math.floor(Math.random() * 90) - 45;

    return {
      targetX: `${targetX}px`,
      rotation: `${rotation}deg`
    };
  }, []);

  // Get a random animation duration class
  const getRandomDuration = useCallback(() => {
    // Slightly faster base durations for "cannon" feel
    const durations = ['duration-1', 'duration-2', 'duration-3'];
    return durations[Math.floor(Math.random() * durations.length)];
  }, []);

  // Create a flying emoji with unique ID and random position
  const createFlyingEmoji = useCallback((emojiType, senderName = userName) => {
    console.log('Creating flying emoji:', emojiType, 'from:', senderName);

    // If flying emojis are disabled, don't create them
    if (!showFlyingEmojis) {
      console.log('Flying emojis disabled, not creating emoji');
      return;
    }

    const trajectory = getTrajectory();
    const durationClass = getRandomDuration();
    
    // Starting position - "Wider mouth"
    // Randomize between 85% and 95% to create a wider source area
    const leftPos = `${Math.floor(Math.random() * 11) + 85}%`;

    const newEmoji = {
      id: Date.now() + Math.random().toString(36).substr(2, 5),
      type: emojiType,
      leftPos,
      emoji: emojis.find((e) => e.id === emojiType)?.emoji || '❤️',
      durationClass,
      ...trajectory, // targetX, rotation
      senderName,
    };

    console.log('New emoji object:', newEmoji);

    setFlyingEmojis((prev) => {
      const updated = [...prev, newEmoji];
      console.log('Updated flying emojis:', updated);
      return updated;
    });

    // Remove the emoji after animation completes
    const animationDurations = {
      'duration-1': 2500,
      'duration-2': 3000, 
      'duration-3': 3500,
    };

    setTimeout(() => {
      setFlyingEmojis((prev) =>
        prev.filter((emoji) => emoji.id !== newEmoji.id)
      );
    }, animationDurations[durationClass]); 
  }, [userName, showFlyingEmojis, getTrajectory, getRandomDuration]);

  // Listen for emoji reactions from Firebase
  useEffect(() => {
    if (!videoID) return;

    console.log('Setting up Firebase listener for reactions');

    const unsubscribe = firestore
      .collection('videos')
      .doc(videoID)
      .collection('reactions')
      .orderBy('timestamp', 'desc')
      .limit(20)
      .onSnapshot(
        (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === 'added') {
              const reaction = change.doc.data();
              
              // Check if reaction has a valid timestamp
              // New local writes might have null timestamp initially (if using estimateTimestamps: false), 
              // but we filter own reactions by userID anyway.
              if (!reaction.timestamp) return;

              // Calculate age of reaction in seconds
              const reactionTime = reaction.timestamp.toDate();
              const ageInSeconds = (new Date() - reactionTime) / 1000;

              // If reaction is older than 15 seconds, consider it "old" and don't animate
              // This prevents emojis from popping up on page load
              if (ageInSeconds > 15) {
                console.log('Ignoring old reaction:', ageInSeconds, 'seconds old');
                
                // If it's significantly old (e.g. > 60s), clean it up from Firestore
                // This handles cases where the original sender failed to delete it
                if (ageInSeconds > 60) {
                   console.log('Cleaning up stuck reaction:', change.doc.id);
                   change.doc.ref.delete().catch(err => console.error("Cleanup error:", err));
                }
                return; 
              }

              console.log('Reaction received:', reaction);
              // Show reactions from other users only if flying emojis are enabled
              if (reaction.userID !== userID && showFlyingEmojis) {
                createFlyingEmoji(
                  reaction.emojiType,
                  reaction.userName || 'Viewer'
                );
              }
            }
            // We don't need to handle 'removed' events since we're just triggering animations
            // when documents are added, and the animations clean themselves up automatically
          });
        },
        (error) => {
          console.error('Error listening to reactions:', error);
        }
      );

    return () => unsubscribe();
  }, [videoID, userID, showFlyingEmojis, createFlyingEmoji]);

  // Send emoji reaction to Firebase
  const sendReaction = async (emojiType) => {
    console.log('Emoji clicked:', emojiType);

    // Show the flying emoji locally immediately
    createFlyingEmoji(emojiType);

    if (!videoID || !userID) {
      console.log('No videoID or userID, skipping Firebase update', {
        videoID,
        userID,
      });
      return;
    }

    try {
      // Add to Firebase
      const docRef = await firestore
        .collection('videos')
        .doc(videoID)
        .collection('reactions')
        .add({
          emojiType,
          userID,
          userName, // Include the user's name
          timestamp: serverTimestamp(),
        });

      console.log('Reaction saved to Firebase with ID:', docRef.id);

      // Set up deletion after animation completes (plus a small buffer)
      // Use the longest animation duration to ensure all clients have seen it
      setTimeout(async () => {
        try {
          await firestore
            .collection('videos')
            .doc(videoID)
            .collection('reactions')
            .doc(docRef.id)
            .delete();
          console.log('Reaction document deleted from Firestore:', docRef.id);
        } catch (error) {
          console.error('Error deleting reaction document:', error);
        }
      }, 5000); // 5 seconds - longer than the longest animation duration to ensure all clients see it
    } catch (error) {
      console.error('Error sending reaction:', error);
    }
  };

  // Toggle emoji tray display
  const toggleEmojiDisplay = () => {
    setShowEmojis(!showEmojis);
  };

  // Toggle flying emojis display
  const toggleFlyingEmojis = () => {
    setShowFlyingEmojis(!showFlyingEmojis);
  };

  // Format display name
  const formatName = (name) => {
    if (!name) return '';
    return name;
  };

  return (
    <div className="emoji-reactions-container mt-2">
      {/* Controls container - aligned to the right */}
      <div className="emoji-controls">
        <div className="reactions-box">
          <button
            className="emoji-toggle-btn"
            onClick={toggleEmojiDisplay}
            title="Hide or display emojis"
          >
            <i className="fas fa-smile"></i>
          </button>
        </div>

        {/* Emoji selection tray */}
        <div className={`reaction-icons ${showEmojis ? '' : 'hide'}`}>
          {emojis.map((emoji) => (
            <button
              key={emoji.id}
              className={`emoji-btn ${!showFlyingEmojis ? 'disabled' : ''}`}
              onClick={() => showFlyingEmojis && sendReaction(emoji.id)}
              title={showFlyingEmojis ? emoji.label : 'Reactions disabled'}
              disabled={!showFlyingEmojis}
            >
              {emoji.emoji}
            </button>
          ))}
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="emoji-display"
              checked={showFlyingEmojis}
              onChange={toggleFlyingEmojis}
              title="Show/hide flying emoji reactions"
            />
          </div>
        </div>
      </div>

      {/* Flying emojis container - full width */}
      <div className="flying-emojis-container">
        {showFlyingEmojis &&
          flyingEmojis.map((emoji) => (
            <div
              key={emoji.id}
              className={`flying-emoji ${emoji.durationClass}`}
              style={{
                left: emoji.leftPos,
                '--target-x': emoji.targetX,
                '--target-rot': emoji.rotation,
              }}
            >
              <div className="emoji-inner">
                <span className="emoji-symbol">{emoji.emoji}</span>
                <span className="sender-name">
                  {formatName(emoji.senderName)}
                </span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default EmojiReactions;
